(() => {
  const ROOT_SELECTOR = '[data-accessory-lp-main]';

  function initGallery(root) {
    const attemptAutoplay = (video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      const playRequest = video.play();
      if (playRequest && typeof playRequest.catch === 'function') playRequest.catch(() => {});
    };

    const videos = Array.from(root.querySelectorAll('[data-hsw5-gallery-slide] video'));
    const playIfVisible = (video) => {
      if (video.dataset.hsw5InViewport === 'true' && video.paused) attemptAutoplay(video);
    };

    videos.forEach((video) => {
      video.autoplay = false;
      video.addEventListener('loadeddata', () => playIfVisible(video), { once: true });
      video.addEventListener('canplay', () => playIfVisible(video), { once: true });
    });

    if ('IntersectionObserver' in window) {
      const videoVisibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const visible = entry.isIntersecting && entry.intersectionRatio > 0;
          video.dataset.hsw5InViewport = String(visible);

          if (visible) playIfVisible(video);
          else if (!video.paused) video.pause();
        });
      }, { threshold: 0 });

      videos.forEach((video) => videoVisibilityObserver.observe(video));
    } else {
      videos.forEach((video) => {
        video.dataset.hsw5InViewport = 'true';
        attemptAutoplay(video);
      });
    }

    const galleryTrack = root.querySelector('[data-hsw5-gallery-track]');
    const gallerySlides = Array.from(root.querySelectorAll('[data-hsw5-gallery-slide]'));
    const thumbnailTrack = root.querySelector('[data-hsw5-gallery-thumbnails]');
    const thumbnails = Array.from(root.querySelectorAll('[data-hsw5-gallery-thumbnail]'));
    const dots = Array.from(root.querySelectorAll('[data-hsw5-gallery-dot]'));
    const previousButton = root.querySelector('[data-hsw5-gallery-previous]');
    const nextButton = root.querySelector('[data-hsw5-gallery-next]');
    const dotsTrack = root.querySelector('[data-hsw5-gallery-dots]');
    const galleryCount = root.querySelector('[data-hsw5-gallery-count]');
    const lightbox = root.querySelector('[data-hsw5-lightbox]');
    const lightboxImage = root.querySelector('[data-hsw5-lightbox-image]');
    const lightboxCount = root.querySelector('[data-hsw5-lightbox-count]');
    const lightboxStage = root.querySelector('[data-hsw5-lightbox-stage]');

    let galleryIndex = 0;
    let galleryScrollFrame;
    let visibleGalleryIndexes = gallerySlides.map((_, index) => index);

    let thumbnailPointerId;
    let thumbnailStartX = 0;
    let thumbnailStartScrollLeft = 0;
    let thumbnailMoved = false;

    let lightboxIndex = 0;
    let lightboxScale = 1;
    let lightboxPointerX;
    let lightboxPointerY;
    let lightboxStartedOutsideImage = false;
    let lightboxTranslateX = 0;
    let lightboxTranslateY = 0;
    let lightboxStartTranslateX = 0;
    let lightboxStartTranslateY = 0;

    function updateThumbnailWindow() {
      if (!thumbnailTrack || !thumbnailTrack.clientWidth) return;

      const visibleThumbnails = thumbnails.filter((thumbnail) => !thumbnail.hidden);
      const firstThumbnail = visibleThumbnails[0];
      const activePosition = visibleGalleryIndexes.indexOf(galleryIndex);

      if (!firstThumbnail || activePosition < 0) return;

      const thumbnailWidth = firstThumbnail.getBoundingClientRect().width;
      const gap = parseFloat(window.getComputedStyle(thumbnailTrack).columnGap) || 0;

      const visibleCount = Math.max(
        1,
        Math.min(
          visibleThumbnails.length,
          Math.floor(
            (thumbnailTrack.clientWidth + gap) /
            (thumbnailWidth + gap)
          )
        )
      );

      const lastWindowStart = Math.max(
        0,
        visibleThumbnails.length - visibleCount
      );

      const windowStart = Math.max(
        0,
        Math.min(
          activePosition - visibleCount + 2,
          lastWindowStart
        )
      );

      const firstVisibleThumbnail = visibleThumbnails[windowStart];

      const scrollLeft =
        firstVisibleThumbnail.offsetLeft -
        firstThumbnail.offsetLeft;

      thumbnailTrack.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }

    function renderGalleryState() {
      const visiblePosition = Math.max(
        0,
        visibleGalleryIndexes.indexOf(galleryIndex)
      );

      const hasMultipleMedia =
        visibleGalleryIndexes.length > 1;

      if (galleryCount) {
        galleryCount.textContent =
          `${visiblePosition + 1} / ${visibleGalleryIndexes.length || 1}`;
      }

      /*
      * Gallery navigation
      *
      * First slide:
      *   Previous = hidden
      *
      * Middle slides:
      *   Previous = visible
      *   Next = visible
      *
      * Last slide:
      *   Next = hidden
      */
      if (previousButton) {
        previousButton.hidden =
          !hasMultipleMedia ||
          visiblePosition === 0;
      }

      if (nextButton) {
        nextButton.hidden =
          !hasMultipleMedia ||
          visiblePosition === visibleGalleryIndexes.length - 1;
      }

      thumbnails.forEach((thumbnail, index) => {
        const active = index === galleryIndex;

        thumbnail.classList.toggle('is-active', active);

        if (active) {
          thumbnail.setAttribute('aria-current', 'true');
        } else {
          thumbnail.removeAttribute('aria-current');
        }
      });

      dots.forEach((dot, index) => {
        const active = index === galleryIndex;
        const dotPosition =
          visibleGalleryIndexes.indexOf(index);

        dot.classList.toggle('is-active', active);

        dot.hidden =
          dotPosition < 0 ||
          Math.abs(dotPosition - visiblePosition) > 2;

        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      updateThumbnailWindow();
    }

    function setActiveSlide(index, behavior = 'auto') {
      if (!galleryTrack || !gallerySlides.length) return;

      if (!visibleGalleryIndexes.length) {
        visibleGalleryIndexes = gallerySlides.map(
          (_, slideIndex) => slideIndex
        );
      }

      galleryIndex = visibleGalleryIndexes.includes(index)
        ? index
        : visibleGalleryIndexes[0];

      galleryTrack.scrollTo({
        left: gallerySlides[galleryIndex].offsetLeft,
        behavior
      });

      renderGalleryState();
    }

    function moveGallery(direction) {
      const currentPosition =
        visibleGalleryIndexes.indexOf(galleryIndex);

      const nextPosition = Math.max(
        0,
        Math.min(
          visibleGalleryIndexes.length - 1,
          currentPosition + direction
        )
      );

      setActiveSlide(
        visibleGalleryIndexes[nextPosition],
        'smooth'
      );
    }

    previousButton?.addEventListener(
      'click',
      () => moveGallery(-1)
    );

    nextButton?.addEventListener(
      'click',
      () => moveGallery(1)
    );

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener(
        'click',
        () => setActiveSlide(index)
      );
    });

    dots.forEach((dot, index) => {
      dot.addEventListener(
        'click',
        () => setActiveSlide(index)
      );
    });

    thumbnailTrack?.addEventListener('pointerdown', (event) => {
      if (
        !event.isPrimary ||
        (event.pointerType === 'mouse' && event.button !== 0)
      ) return;

      thumbnailPointerId = event.pointerId;
      thumbnailStartX = event.clientX;
      thumbnailStartScrollLeft = thumbnailTrack.scrollLeft;
      thumbnailMoved = false;

      thumbnailTrack.classList.add('is-dragging');
    });

    thumbnailTrack?.addEventListener('pointermove', (event) => {
      if (event.pointerId !== thumbnailPointerId) return;

      const distance = event.clientX - thumbnailStartX;

      if (Math.abs(distance) > 4 && !thumbnailMoved) {
        thumbnailMoved = true;
        thumbnailTrack.setPointerCapture(event.pointerId);
      }

      if (!thumbnailMoved) return;

      event.preventDefault();

      thumbnailTrack.scrollLeft =
        thumbnailStartScrollLeft - distance;
    });

    const endThumbnailDrag = (event) => {
      if (event.pointerId !== thumbnailPointerId) return;

      if (thumbnailTrack.hasPointerCapture(event.pointerId)) {
        thumbnailTrack.releasePointerCapture(event.pointerId);
      }

      thumbnailPointerId = undefined;

      thumbnailTrack.classList.remove('is-dragging');
    };

    thumbnailTrack?.addEventListener(
      'pointerup',
      endThumbnailDrag
    );

    thumbnailTrack?.addEventListener(
      'pointercancel',
      endThumbnailDrag
    );

    thumbnailTrack?.addEventListener(
      'click',
      (event) => {
        if (!thumbnailMoved) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        thumbnailMoved = false;
      },
      { capture: true }
    );

    galleryTrack?.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(galleryScrollFrame);

        galleryScrollFrame = requestAnimationFrame(() => {
          if (!visibleGalleryIndexes.length) return;

          const nextIndex =
            visibleGalleryIndexes.reduce(
              (closestIndex, index) => (
                Math.abs(
                  gallerySlides[index].offsetLeft -
                  galleryTrack.scrollLeft
                ) <
                Math.abs(
                  gallerySlides[closestIndex].offsetLeft -
                  galleryTrack.scrollLeft
                )
                  ? index
                  : closestIndex
              ),
              visibleGalleryIndexes[0]
            );

          if (nextIndex === galleryIndex) return;

          galleryIndex = nextIndex;

          renderGalleryState();
        });
      },
      { passive: true }
    );

    if (galleryTrack && 'ResizeObserver' in window) {
      new ResizeObserver(
        () => setActiveSlide(galleryIndex, 'auto')
      ).observe(galleryTrack);
    }

    function renderLightbox() {
      if (!lightboxImage || !visibleGalleryIndexes.length) return;

      if (!visibleGalleryIndexes.includes(lightboxIndex)) {
        lightboxIndex = visibleGalleryIndexes[0];
      }

      const sourceImage =
        gallerySlides[lightboxIndex]?.querySelector('img') ||
        thumbnails[lightboxIndex]?.querySelector('img');

      if (!sourceImage) return;

      lightboxImage.src =
        sourceImage.currentSrc ||
        sourceImage.src;

      const visiblePosition =
        visibleGalleryIndexes.indexOf(lightboxIndex);

      lightboxImage.alt =
        sourceImage.alt ||
        `Product media ${visiblePosition + 1}`;

      lightboxImage.style.transform =
        `translate(${lightboxTranslateX}px, ${lightboxTranslateY}px) scale(${lightboxScale})`;

      if (lightboxCount) {
        lightboxCount.textContent =
          `${visiblePosition + 1} / ${visibleGalleryIndexes.length}`;
      }
    }

    function resetLightboxTransform() {
      lightboxScale = 1;
      lightboxTranslateX = 0;
      lightboxTranslateY = 0;
    }

    function openLightbox() {
      if (
        !lightbox ||
        !lightboxImage ||
        !visibleGalleryIndexes.length
      ) return;

      lightboxIndex = galleryIndex;

      resetLightboxTransform();
      renderLightbox();

      document.body.style.overflow = 'hidden';

      if (typeof lightbox.showModal === 'function') {
        lightbox.showModal();
      } else {
        lightbox.setAttribute('open', '');
      }
    }

    function closeLightbox() {
      if (!lightbox) return;

      if (typeof lightbox.close === 'function') {
        lightbox.close();
      } else {
        lightbox.removeAttribute('open');
      }

      document.body.style.overflow = '';
    }

    function moveLightbox(direction) {
      const currentPosition =
        visibleGalleryIndexes.indexOf(lightboxIndex);

      const nextPosition =
        (
          currentPosition +
          direction +
          visibleGalleryIndexes.length
        ) % visibleGalleryIndexes.length;

      lightboxIndex =
        visibleGalleryIndexes[nextPosition];

      resetLightboxTransform();
      renderLightbox();
    }

    galleryTrack?.addEventListener('click', (event) => {
      if (
        event.target.closest(
          'video, iframe, model-viewer, button'
        )
      ) return;

      openLightbox();
    });

    root
      .querySelector('[data-hsw5-lightbox-close]')
      ?.addEventListener('click', closeLightbox);

    root
      .querySelector('[data-hsw5-lightbox-previous]')
      ?.addEventListener(
        'click',
        () => moveLightbox(-1)
      );

    root
      .querySelector('[data-hsw5-lightbox-next]')
      ?.addEventListener(
        'click',
        () => moveLightbox(1)
      );

    lightbox?.addEventListener('close', () => {
      document.body.style.overflow = '';
    });

    lightboxStage?.addEventListener('dblclick', (event) => {
      event.preventDefault();

      if (!lightboxImage?.src) return;

      lightboxScale =
        lightboxScale > 1
          ? 1
          : 2.4;

      lightboxTranslateX = 0;
      lightboxTranslateY = 0;

      renderLightbox();
    });

    lightboxStage?.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();

        lightboxScale = Math.min(
          4,
          Math.max(
            1,
            lightboxScale +
            (event.deltaY < 0 ? 0.2 : -0.2)
          )
        );

        if (lightboxScale === 1) {
          lightboxTranslateX = 0;
          lightboxTranslateY = 0;
        }

        renderLightbox();
      },
      { passive: false }
    );

    lightboxStage?.addEventListener(
      'pointerdown',
      (event) => {
        lightboxPointerX = event.clientX;
        lightboxPointerY = event.clientY;

        lightboxStartedOutsideImage =
          event.target === lightboxStage;

        lightboxStartTranslateX =
          lightboxTranslateX;

        lightboxStartTranslateY =
          lightboxTranslateY;

        lightboxStage.setPointerCapture(
          event.pointerId
        );
      }
    );

    lightboxStage?.addEventListener(
      'pointermove',
      (event) => {
        if (
          lightboxPointerX == null ||
          lightboxScale <= 1
        ) return;

        lightboxTranslateX =
          lightboxStartTranslateX +
          event.clientX -
          lightboxPointerX;

        lightboxTranslateY =
          lightboxStartTranslateY +
          event.clientY -
          lightboxPointerY;

        renderLightbox();
      }
    );

    lightboxStage?.addEventListener(
      'pointerup',
      (event) => {
        if (lightboxPointerX == null) return;

        const horizontalDistance =
          event.clientX -
          lightboxPointerX;

        const verticalDistance =
          event.clientY -
          lightboxPointerY;

        const shouldClose =
          lightboxStartedOutsideImage &&
          Math.abs(horizontalDistance) < 8 &&
          Math.abs(verticalDistance) < 8;

        lightboxPointerX = undefined;
        lightboxPointerY = undefined;
        lightboxStartedOutsideImage = false;

        if (shouldClose) {
          closeLightbox();
          return;
        }

        if (
          lightboxScale <= 1 &&
          Math.abs(horizontalDistance) > 50
        ) {
          moveLightbox(
            horizontalDistance > 0
              ? -1
              : 1
          );
        }
      }
    );

    lightboxStage?.addEventListener(
      'pointercancel',
      () => {
        lightboxPointerX = undefined;
        lightboxPointerY = undefined;
        lightboxStartedOutsideImage = false;
      }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (!lightbox?.open) return;

        if (event.key === 'ArrowLeft') {
          moveLightbox(-1);
        }

        if (event.key === 'ArrowRight') {
          moveLightbox(1);
        }
      }
    );

    const compactToken = (value) =>
      String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

    const filenameTokens = (value) => {
      const normalized =
        `-${String(value)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')}-`;

      return normalized
        .replace(/-black-watch-/g, '-')
        .replace(/-rose-?gold-watch-/g, '-')
        .replace(/-+/g, '-');
    };

    const colorFilenameTokens = (value) => {
      const hyphenated =
        String(value)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');

      return [
        ...new Set(
          [
            hyphenated,
            compactToken(value)
          ].filter(Boolean)
        )
      ];
    };

    const compactSize = (value) => {
      const millimeterSize =
        String(value).match(/(\d+)\s*mm/i);

      return millimeterSize
        ? `${millimeterSize[1]}mm`
        : compactToken(value);
    };

    function filterByOptions(
      optionGroups,
      gallerySize = '',
      galleryColor = '',
      protectedMediaId = null
    ) {
      const colorGroup =
        optionGroups.find(
          (group) =>
            /colou?r/.test(
              group.dataset.optionName || ''
            )
        );

      const sizeGroup =
        optionGroups.find(
          (group) =>
            /size/.test(
              group.dataset.optionName || ''
            )
        );

      const colorOptions =
        colorGroup
          ? Array.from(
              colorGroup.querySelectorAll(
                '[data-lp-option]'
              )
            )
              .map((input) => ({
                color: compactToken(input.value),
                filenameTokens:
                  colorFilenameTokens(input.value)
              }))
              .filter((option) => option.color)
          : [];

      const sizeOptions =
        sizeGroup
          ? [
              ...new Set(
                Array.from(
                  sizeGroup.querySelectorAll(
                    '[data-lp-option]'
                  )
                )
                  .map((input) =>
                    compactSize(input.value)
                  )
                  .filter(Boolean)
              )
            ]
          : [];

      const imageColor = (slide) => {
        const filename =
          filenameTokens(
            slide.dataset.imageFilename || ''
          );

        return colorOptions
          .map((option) => {
            const positions =
              option.filenameTokens
                .map((token) =>
                  filename.indexOf(
                    `-${token}-`
                  )
                )
                .filter(
                  (position) =>
                    position >= 0
                );

            return {
              color: option.color,
              position:
                positions.length
                  ? Math.min(...positions)
                  : -1
            };
          })
          .filter(
            (match) =>
              match.position >= 0
          )
          .sort(
            (a, b) =>
              a.position - b.position ||
              b.color.length - a.color.length
          )[0]?.color || '';
      };

      const imageSize = (slide) => {
        const filename =
          filenameTokens(
            slide.dataset.imageFilename || ''
          );

        return (
          sizeOptions.find(
            (size) =>
              filename.includes(
                `-${size}-`
              )
          ) || ''
        );
      };

      const selectedColor =
        compactToken(
          colorGroup
            ?.querySelector(
              '[data-lp-option]:checked'
            )
            ?.value ||
          galleryColor
        );

      const selectedSize =
        compactSize(
          sizeGroup
            ?.querySelector(
              '[data-lp-option]:checked'
            )
            ?.value ||
          gallerySize
        );

      const shouldFilter =
        Boolean(
          selectedColor ||
          selectedSize
        );

      const matchingIndexes =
        shouldFilter
          ? gallerySlides.reduce(
              (indexes, slide, index) => {
                const isProtectedMedia =
                  protectedMediaId &&
                  Number(slide.dataset.mediaId) ===
                    Number(protectedMediaId);

                const slideColor =
                  imageColor(slide);

                const slideSize =
                  imageSize(slide);

                const matchesColor =
                  !slideColor ||
                  !selectedColor ||
                  slideColor === selectedColor;

                const matchesSize =
                  !slideSize ||
                  !selectedSize ||
                  slideSize === selectedSize;

                if (
                  isProtectedMedia ||
                  (
                    matchesColor &&
                    matchesSize
                  )
                ) {
                  indexes.push(index);
                }

                return indexes;
              },
              []
            )
          : [];

      visibleGalleryIndexes =
        matchingIndexes.length
          ? matchingIndexes
          : gallerySlides.map(
              (_, index) => index
            );

      gallerySlides.forEach(
        (slide, index) => {
          slide.hidden =
            !visibleGalleryIndexes.includes(
              index
            );
        }
      );

      [...thumbnails, ...dots].forEach(
        (button) => {
          button.hidden =
            !visibleGalleryIndexes.includes(
              Number(button.dataset.index)
            );
        }
      );

      const hasMultipleMedia =
        visibleGalleryIndexes.length > 1;

      if (previousButton) {
        previousButton.hidden =
          !hasMultipleMedia;
      }

      if (nextButton) {
        nextButton.hidden =
          !hasMultipleMedia;
      }

      if (dotsTrack) {
        dotsTrack.hidden =
          !hasMultipleMedia;
      }

      if (thumbnailTrack) {
        thumbnailTrack.hidden =
          !hasMultipleMedia;
      }

      root
        .querySelectorAll(
          '[data-hsw5-lightbox-previous], [data-hsw5-lightbox-next]'
        )
        .forEach((button) => {
          button.hidden =
            !hasMultipleMedia;
        });

      setActiveSlide(
        visibleGalleryIndexes.includes(
          galleryIndex
        )
          ? galleryIndex
          : visibleGalleryIndexes[0]
      );
    }

    renderGalleryState();

    return {
      filterByOptions,
      gallerySlides,
      setActiveSlide
    };
  }

  function shippingDate(root) {
    const days = Math.max(
      1,
      Math.floor(
        Number(root.dataset.shippingDays) || 1
      )
    );

    const excludeWeekends =
      root.dataset.excludeShippingWeekends ===
      'true';

    const date = new Date();

    let elapsedDays = 0;

    while (elapsedDays < days) {
      date.setDate(date.getDate() + 1);

      const weekend =
        date.getDay() === 0 ||
        date.getDay() === 6;

      if (
        !excludeWeekends ||
        !weekend
      ) {
        elapsedDays += 1;
      }
    }

    return date.toLocaleDateString(
      'en-US',
      {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      }
    );
  }

  function initProductForm(root, gallery) {
    const form =
      root.querySelector(
        '.accessory-lp__form'
      );

    const variantsElement =
      root.querySelector(
        '[data-lp-variants]'
      );

    if (!form || !variantsElement) return;

    let variants = [];

    try {
      variants =
        JSON.parse(
          variantsElement.textContent
        );
    } catch (error) {
      console.error(
        'Accessory LP variant data could not be parsed.',
        error
      );

      return;
    }

    const groups =
      Array.from(
        form.querySelectorAll(
          '[data-option-position]'
        )
      );

    const variantId =
      form.querySelector(
        '[data-lp-variant-id]'
      );

    const atc =
      form.querySelector(
        '[data-lp-atc]'
      );

    const atcText =
      form.querySelector(
        '[data-lp-atc-text]'
      );

    const sticky =
      root.querySelector(
        '[data-lp-sticky]'
      );

    const stickySubmit =
      root.querySelector(
        '[data-lp-sticky-submit]'
      );

    const shipDate =
      shippingDate(root);

    root
      .querySelectorAll(
        '[data-lp-ship-date]'
      )
      .forEach((element) => {
        element.textContent =
          shipDate;
      });

    const selectedOptions = () =>
      groups.map(
        (group) =>
          group.querySelector(
            '[data-lp-option]:checked'
          )?.value || ''
      );

    const findVariant = () => {
      if (groups.length === 0) {
        return variants[0] || null;
      }

      const selected =
        selectedOptions();

      if (
        selected.some(
          (value) => !value
        )
      ) {
        return null;
      }

      return (
        variants.find(
          (variant) =>
            variant.options.every(
              (value, index) =>
                value ===
                selected[index]
            )
        ) || null
      );
    };

    const colorGroup =
      groups.find(
        (group) =>
          /colou?r/.test(
            group.dataset.optionName || ''
          )
      );

    const sizeGroup =
      groups.find(
        (group) =>
          /size/.test(
            group.dataset.optionName || ''
          )
      );

    const colorIndex =
      colorGroup
        ? groups.indexOf(colorGroup)
        : -1;

    const sizeIndex =
      sizeGroup
        ? groups.indexOf(sizeGroup)
        : -1;

    const initiallyUnselectedOptionNames =
      new Set(
        String(
          root.dataset.initiallyUnselectedOptions || ''
        )
          .split(',')
          .map((value) =>
            value
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '')
          )
          .filter(Boolean)
      );

    const isInitiallyUnselectedGroup =
      (group) => initiallyUnselectedOptionNames.has(
        String(group?.dataset.optionName || '')
          .trim()
          .toLowerCase()
      );

    let defaultGallerySize = '';
    let defaultGalleryColor = '';

    function setGroupValue(
      group,
      value
    ) {
      if (!group) return false;

      let matched = false;

      group
        .querySelectorAll(
          '[data-lp-option]'
        )
        .forEach((input) => {
          const shouldCheck =
            String(input.value) ===
            String(value);

          input.checked =
            shouldCheck;

          if (shouldCheck) {
            matched = true;
          }
        });

      return matched;
    }

    function selectVariantOptions(
      variant
    ) {
      groups.forEach(
        (group, index) => {
          setGroupValue(
            group,
            variant.options[index]
          );
        }
      );
    }

    function selectInitialVariantOptions(
      variant
    ) {
      groups.forEach(
        (group, index) => {
          if (
            isInitiallyUnselectedGroup(group)
          ) {
            setGroupValue(group, '');
            return;
          }

          setGroupValue(
            group,
            variant.options[index]
          );
        }
      );
    }

    function clearOptionSelections() {
      groups.forEach((group) => {
        group
          .querySelectorAll(
            '[data-lp-option]'
          )
          .forEach((input) => {
            input.checked = false;
          });
      });
    }

    function setColorOptionAvailability(
      input,
      unavailable,
      soldOut = false,
      disabled = unavailable || soldOut,
      availableInOtherSize = false
    ) {
      input.hidden =
        false;

      input.disabled =
        disabled;

      input.setAttribute(
        'aria-disabled',
        disabled ? 'true' : 'false'
      );

      const wrapper =
        input.closest(
          '[data-lp-option-item]'
        );

      if (
        wrapper &&
        wrapper !== colorGroup
      ) {
        wrapper.hidden =
          false;

        wrapper.classList.toggle(
          'is-unavailable',
          unavailable
        );

        wrapper.classList.toggle(
          'is-sold-out',
          soldOut
        );

        wrapper.classList.toggle(
          'is-available-in-other-size',
          availableInOtherSize
        );
      }

      const wrappingLabel =
        input.closest('label');

      if (
        wrappingLabel &&
        wrappingLabel !== colorGroup
      ) {
        wrappingLabel.hidden =
          false;

        wrappingLabel.classList.toggle(
          'is-unavailable',
          unavailable
        );

        wrappingLabel.classList.toggle(
          'is-sold-out',
          soldOut
        );

        wrappingLabel.classList.toggle(
          'is-available-in-other-size',
          availableInOtherSize
        );
      }

      if (
        input.id &&
        colorGroup
      ) {
        colorGroup
          .querySelectorAll('label')
          .forEach((label) => {
            if (
              label.htmlFor ===
              input.id
            ) {
              label.hidden =
                false;

              label.classList.toggle(
                'is-unavailable',
                unavailable
              );

              label.classList.toggle(
                'is-sold-out',
                soldOut
              );

              label.classList.toggle(
                'is-available-in-other-size',
                availableInOtherSize
              );
            }
          });
      }
    }

    function updateColorSwatchAvailability(
      selectedSize = ''
    ) {
      if (
        !colorGroup ||
        colorIndex < 0
      ) {
        return;
      }

      const colorInputs =
        Array.from(
          colorGroup.querySelectorAll(
            '[data-lp-option]'
          )
        );

      colorInputs.forEach(
        (input) => {
          const matchingVariants =
            variants.filter(
              (variant) =>
                String(
                  variant.options[colorIndex]
                ) ===
                  String(input.value)
            );

          const exists =
            matchingVariants.length > 0;

          const globallyAvailable =
            matchingVariants.some(
              (variant) => variant.available
            );

          const soldOut =
            exists &&
            !globallyAvailable;

          const availableForSelectedSize =
            !selectedSize ||
            matchingVariants.some(
              (variant) =>
                variant.available &&
                String(
                  variant.options[sizeIndex]
                ) === String(selectedSize)
            );

          const availableInOtherSize =
            Boolean(selectedSize) &&
            globallyAvailable &&
            !availableForSelectedSize;

          const unavailable =
            !exists ||
            soldOut ||
            availableInOtherSize;

          const disabled =
            !exists ||
            soldOut;

          setColorOptionAvailability(
            input,
            unavailable,
            soldOut,
            disabled,
            availableInOtherSize
          );
        }
      );
    }

    function findAvailableVariantForOption(
      optionIndex,
      optionValue
    ) {
      const selected =
        selectedOptions();

      return variants
        .filter(
          (variant) =>
            variant.available &&
            String(
              variant.options[optionIndex]
            ) === String(optionValue)
        )
        .sort((variantA, variantB) => {
          const matchingSelectionCount =
            (variant) =>
              variant.options.reduce(
                (count, value, index) =>
                  index !== optionIndex &&
                  selected[index] &&
                  String(value) ===
                    String(selected[index])
                    ? count + 1
                    : count,
                0
              );

          return (
            matchingSelectionCount(variantB) -
            matchingSelectionCount(variantA)
          );
        })[0] || null;
    }

    function updateVariantUrl(
      variant
    ) {
      if (!variant) return;

      const url =
        new URL(
          window.location.href
        );

      url.searchParams.set(
        'variant',
        variant.id
      );

      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    }

    function renderVariant(
      variant,
      gallerySize = '',
      galleryColor = '',
      visualVariant = variant
    ) {
      gallery?.filterByOptions(
        groups,
        gallerySize,
        galleryColor,
        visualVariant?.mediaId || null
      );

      const complete =
        groups.length === 0 ||
        selectedOptions().every(Boolean);

      const available =
        Boolean(
          variant?.available
        );

      variantId.value =
        variant?.id || '';

      atc.disabled =
        !complete ||
        !available;

      stickySubmit.disabled =
        !complete ||
        !available;

      const hasSelectedSize =
        Boolean(
          sizeGroup?.querySelector(
            '[data-lp-option]:checked'
          )
        );

      const hasSelectedColor =
        Boolean(
          colorGroup?.querySelector(
            '[data-lp-option]:checked'
          )
        );

      const buttonText =
        !complete
          ? !hasSelectedSize
            ? 'Select Size'
            : !hasSelectedColor
              ? 'Select Color'
              : 'Select options'
          : available
            ? 'Add to Cart'
            : 'Sold out';

      atcText.innerHTML =
        complete && available
          ? `${buttonText}<i aria-hidden="true"></i>${variant.price}`
          : buttonText;

      stickySubmit.textContent =
        buttonText;

      groups.forEach((group) => {
        const label =
          group.querySelector(
            '[data-lp-option-label]'
          );

        const checked =
          group.querySelector(
            '[data-lp-option]:checked'
          );

        if (
          label &&
          checked
        ) {
          label.textContent =
            checked.value;
        }
      });

      const displayedVariant =
        variant || visualVariant;

      if (!displayedVariant) return;

      root.querySelector(
        '[data-lp-price]'
      ).innerHTML =
        displayedVariant.price;

      const compare =
        root.querySelector(
          '[data-lp-compare]'
        );

      compare.innerHTML =
        displayedVariant.compare || '';

      compare.hidden =
        !displayedVariant.compare;

      const saving =
        root.querySelector(
          '[data-lp-saving]'
        );

      saving.textContent =
        displayedVariant.saving
          ? `Save ${displayedVariant.saving}%`
          : '';

      saving.hidden =
        !displayedVariant.saving;

      root.querySelector(
        '[data-lp-sticky-price]'
      ).innerHTML =
        displayedVariant.price;

      const stickyImage =
        sticky?.querySelector('img');

      if (
        stickyImage &&
        displayedVariant.image
      ) {
        stickyImage.removeAttribute(
          'srcset'
        );

        stickyImage.removeAttribute(
          'sizes'
        );

        stickyImage.src =
          displayedVariant.image;
      }

      if (
        displayedVariant.mediaId &&
        gallery
      ) {
        const mediaIndex =
          gallery.gallerySlides.findIndex(
            (slide) =>
              Number(
                slide.dataset.mediaId
              ) ===
              Number(
                displayedVariant.mediaId
              )
          );

        if (mediaIndex >= 0) {
          gallery.setActiveSlide(
            mediaIndex
          );
        }
      }
    }

    form.addEventListener(
      'change',
      (event) => {
        if (
          !event.target.matches(
            '[data-lp-option]'
          )
        ) return;

        const changedGroup =
          event.target.closest(
            '[data-option-position]'
          );

        let variant =
          findVariant();

        if (
          changedGroup === colorGroup &&
          (!variant || !variant.available)
        ) {
          const availableColorVariant =
            findAvailableVariantForOption(
              colorIndex,
              event.target.value
            );

          if (availableColorVariant) {
            selectVariantOptions(
              availableColorVariant
            );

            variant =
              availableColorVariant;
          }
        }

        const selectedSizeValue =
          sizeGroup
            ?.querySelector(
              '[data-lp-option]:checked'
            )
            ?.value || '';

        updateColorSwatchAvailability(
          selectedSizeValue
        );

        /*
         * Until the customer actually selects
         * a Size, keep using the initial
         * gallery-only variant filters.
         *
         * Nothing is visually selected in the
         * Size selector.
         */
        const hasSelectedSize =
          Boolean(
            sizeGroup
              ?.querySelector(
                '[data-lp-option]:checked'
              )
          );

        renderVariant(
          variant,
          hasSelectedSize
            ? ''
            : defaultGallerySize,
          hasSelectedSize
            ? ''
            : defaultGalleryColor
        );

        /*
         * Only update the URL once the selected
         * options resolve to an actual variant.
         */
        if (variant) {
          updateVariantUrl(
            variant
          );
        }
      }
    );

    form.addEventListener(
      'submit',
      (event) => {
        if (!findVariant()) {
          event.preventDefault();
        }
      }
    );

    stickySubmit?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        if (!atc || atc.disabled) return;

        // The theme and cart apps attach behavior to the real form button's
        // click event. requestSubmit() skips that event and falls through to
        // Shopify's native product-form navigation.
        atc.click();
      }
    );

    if (
      atc &&
      sticky &&
      'IntersectionObserver' in window
    ) {
      new IntersectionObserver(
        ([entry]) => {
          sticky.hidden =
            entry.boundingClientRect.bottom >= 0;
        }
      ).observe(atc);
    }

    /*
     * INITIAL VARIANT STATE
     */

    const urlVariantId =
      new URL(
        window.location.href
      ).searchParams.get(
        'variant'
      );

    const urlVariant =
      urlVariantId
        ? (
            variants.find(
              (variant) =>
                String(variant.id) ===
                String(urlVariantId)
            ) || null
          )
        : null;

    /*
     * A valid variant exists in the URL. Use it for every option except the
     * groups merchants explicitly configured to remain initially unselected.
     */
    if (urlVariant) {
      clearOptionSelections();
      selectInitialVariantOptions(
        urlVariant
      );

      defaultGallerySize =
        sizeIndex >= 0
          ? urlVariant.options[sizeIndex] || ''
          : '';
      defaultGalleryColor =
        colorIndex >= 0
          ? urlVariant.options[colorIndex] || ''
          : '';

      const selectedSizeValue =
        sizeGroup
          ?.querySelector(
            '[data-lp-option]:checked'
          )
          ?.value || '';
      const selectedColorValue =
        colorGroup
          ?.querySelector(
            '[data-lp-option]:checked'
          )
          ?.value || '';

      updateColorSwatchAvailability(
        selectedSizeValue
      );
      renderVariant(
        findVariant(),
        selectedSizeValue
          ? ''
          : defaultGallerySize,
        selectedColorValue
          ? ''
          : defaultGalleryColor,
        urlVariant
      );

      return;
    }

    /*
     * Product has no options.
     */
    if (
      groups.length === 0 &&
      variants[0]
    ) {
      renderVariant(
        variants[0]
      );

      return;
    }

    /*
     * No variant in URL. Use the first available variant as the visual
     * context, while respecting any initially unselected option groups.
     */
    clearOptionSelections();

    /*
      * No ?variant= in the URL.
      *
      * Use the FIRST actual Shopify variant
      * as the gallery-only selection.
      *
      * IMPORTANT:
      *
     * Its configured option values are checked in the variant picker unless
     * their option group is configured to remain initially unselected.
     */
    const firstColorValue =
      colorGroup
        ?.querySelector(
          '[data-lp-option]'
        )
        ?.value || '';

    const variantsForFirstColor =
      colorIndex >= 0 && firstColorValue
        ? variants.filter(
            (variant) =>
              String(
                variant.options[colorIndex]
              ) === String(firstColorValue)
          )
        : variants;

    const defaultGalleryVariant =
      variantsForFirstColor.find(
        (variant) => variant.available
      ) ||
      variantsForFirstColor[0] ||
      variants[0] ||
      null;

    defaultGallerySize =
      (
        defaultGalleryVariant &&
        sizeIndex >= 0
      )
        ? (
            defaultGalleryVariant
              .options[sizeIndex] || ''
          )
        : '';

    defaultGalleryColor =
      (
        defaultGalleryVariant &&
        colorIndex >= 0
      )
        ? (
            defaultGalleryVariant
              .options[colorIndex] || ''
          )
        : '';

    if (defaultGalleryVariant) {
      selectInitialVariantOptions(
        defaultGalleryVariant
      );
    }

    const initialSelectedSize =
      sizeGroup
        ?.querySelector(
          '[data-lp-option]:checked'
        )
        ?.value || '';
    const initialSelectedColor =
      colorGroup
        ?.querySelector(
          '[data-lp-option]:checked'
        )
        ?.value || '';

    updateColorSwatchAvailability(
      initialSelectedSize
    );

    /*
     * Gallery is filtered using the first
     * available variant for the first Color.
      *
     * The visual variant may still provide gallery filters when one or more
     * required picker groups intentionally remain unselected.
     */
    renderVariant(
      findVariant(),
      initialSelectedSize
        ? ''
        : defaultGallerySize,
      initialSelectedColor
        ? ''
        : defaultGalleryColor,
      defaultGalleryVariant
    );
  }

  function init(root) {
    if (
      !root ||
      root.dataset.accessoryLpReady ===
        'true'
    ) {
      return;
    }

    root.dataset.accessoryLpReady =
      'true';

    const gallery =
      initGallery(root);

    initProductForm(
      root,
      gallery
    );
  }

  function initAll(
    scope = document
  ) {
    if (
      scope.matches?.(
        ROOT_SELECTOR
      )
    ) {
      init(scope);
    }

    scope
      .querySelectorAll?.(
        ROOT_SELECTOR
      )
      .forEach(init);
  }

  if (
    !window.__accessoryLpMainBound
  ) {
    window.__accessoryLpMainBound =
      true;

    if (
      document.readyState ===
      'loading'
    ) {
      document.addEventListener(
        'DOMContentLoaded',
        () => initAll(),
        { once: true }
      );
    } else {
      initAll();
    }

    document.addEventListener(
      'shopify:section:load',
      (event) =>
        initAll(event.target)
    );
  } else {
    initAll();
  }
})();
